import { CommonModule } from "@angular/common";
import { Component, EventEmitter, HostListener, Input, Output } from "@angular/core";
import { NotificationSoundService, SoundKind } from "src/app/service/notification-sound.service";

interface SettingsTab {
  id: string;
  label: string;
  icon: string;
}

/**
 * Notification settings for this device: each family (chat, tasks, finance,
 * leads, platform) can be switched off, and has its own chime or none
 * (per founder, 20 Oct 2026). Stored per person and device; the push service
 * worker gets a copy so a family switched off is not shown as a push either.
 */
@Component({
  selector: "app-settings-modal",
  templateUrl: "./settings-modal.component.html",
  styleUrls: ["./settings-modal.component.css"],
  imports: [CommonModule],
})
export class SettingsModalComponent {
  @Input() isOpen = false;
  @Output() closed = new EventEmitter<void>();

  readonly tabs: SettingsTab[] = [{ id: "sound", label: "Notifications", icon: "notifications" }];
  activeTab = "sound";
  /** Which family's chime list is open. */
  openKind: SoundKind | null = null;

  constructor(public soundService: NotificationSoundService) {}

  @HostListener("document:keydown.escape")
  onEscape(): void {
    if (this.isOpen) this.onClose();
  }

  onClose(): void {
    this.soundService.stopPreview();
    this.closed.emit();
  }

  selectTab(tabId: string): void {
    this.soundService.stopPreview();
    this.activeTab = tabId;
  }

  toggleEnabled(kind: SoundKind): void {
    this.soundService.setEnabled(kind, !this.soundService.isEnabled(kind));
  }

  toggleKindOpen(kind: SoundKind): void {
    this.soundService.stopPreview();
    this.openKind = this.openKind === kind ? null : kind;
  }

  /** '' = no sound for this family. */
  selectSound(kind: SoundKind, soundId: string): void {
    if (!soundId) {
      this.soundService.setMuted(kind, true);
      return;
    }
    this.soundService.setMuted(kind, false);
    this.soundService.setSound(kind, soundId);
  }

  currentSoundLabel(kind: SoundKind): string {
    if (this.soundService.isMuted(kind)) return "No sound";
    return this.soundService.soundOptions.find((o) => o.id === this.soundService.soundIdOf(kind))?.label || "Chime";
  }

  previewOption(soundId: string, kind: SoundKind, event: Event): void {
    event.stopPropagation();
    this.soundService.previewSound(soundId, kind);
  }

  // kept for any older template bindings
  toggleChatSound(): void {
    this.soundService.setChatMuted(!this.soundService.isChatMuted);
  }
  toggleLeadSound(): void {
    this.soundService.setLeadMuted(!this.soundService.isLeadMuted);
  }
}
